import { FC, useContext, useState, useEffect, useCallback, useRef, MouseEvent, useId } from "react";
import { AppBar, Autocomplete, Box, Button, Divider, Grid, LinearProgress, Menu, MenuItem, Paper, styled, TextField, Toolbar, Typography } from "@mui/material";
import { WindowPortal, Text, InputOnlyField, BasicAlert, IValidationError } from "@ngt/forms";

import { } from 'react-use'
import usePatients from "@/hooks/data/usePatients";
import { useParams } from "react-router-dom";
import { IPatient, ITrial } from "@/api/dtos";
import { ISourceDataConfiguration, ITrialFormMetadata } from '../../../../api/dtos';
import { faChevronDown, faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormActions } from '@ngt/forms-core';
import useSourceDataPreviewByTrialCodeAndPatientStudyNumber from "@/hooks/data/useSourceDataPreviewByTrialCodeAndPatientStudyNumber";
import { LoadingButton } from "@mui/lab";
import SourceDataForm from "../../sourceData/SourceDataForm";

const Page = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const Buffer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...(theme.mixins.toolbar as any),
    '@media (min-width: 600px)': {
        minHeight: '64px'
    }
}));

export interface IConfigurationFormPreviewProps {
    trial?: ITrial;
    metadata?: ITrialFormMetadata;
}

const previewStyling = () => {
    const styles =
`
    html {
        height: 100%;
    }

    body {
        height: 100%;
        overflow: auto;
    }

    body > div:nth-of-type(2) {
        height: 100%;
        overflow: auto;
    }
`;

    const s = document.createElement("style");
    s.appendChild(document.createTextNode(styles));

    const newNodes: Node[] = [];

    newNodes.push(s);
    return newNodes;
}

const ConfigurationFormPreview: FC<IConfigurationFormPreviewProps> = ({
    trial
}) => {
    const actions = useFormActions<{configuration: ISourceDataConfiguration}, IValidationError>();
    const windowRef = useRef<Window | null>(null);

    const previewId = useId();

    
    const channelRef = useRef<BroadcastChannel>();

    useEffect(() => {
        const channel = new BroadcastChannel(previewId ?? 'NO ID');
    
        channel.onmessage = ((ev: MessageEvent<{ type: string, configuration?: ISourceDataConfiguration }>) => {
            if (ev.data.type === 'request-configuration') {
                channel.postMessage({type: 'configuration-update', configuration: actions.getValue()?.configuration ?? undefined })
            }
        });

        channelRef.current = channel;

        return () => {
            channel.close();
            channelRef.current = undefined;
        }

    }, [previewId, actions.getValue]);

    useEffect(() => {
        const removeWindow = () => {
            windowRef.current?.close();
        };

        window.addEventListener("beforeunload", removeWindow);

        return () => {
            window.removeEventListener("beforeunload", removeWindow);
            windowRef.current?.close();
        }
    }, [])

    const onOpen = useCallback(() => {
        if (windowRef.current && !windowRef.current.closed) {
            windowRef.current?.focus();
            return;
        }

        const width: number = 800;
        const height: number = 600;
        const top: number = 0;
        const left: number = 0;

        windowRef.current = window.open(`/${trial?.code}/configurations/preview?previewId=${previewId}`, previewId, 'toolbar=0,location=0,scrollbars=1,statusbar=1,menubar=0,resizable=1,width=' + (width ?? 800) + ',height=' + (height ?? 600) + ',left=' + (left ?? 0) + ',top=' + (top ?? 0) + '');

        windowRef.current?.focus();

        windowRef.current!.onclose = () => {
            windowRef.current = null;
        }

    }, [windowRef, previewId]);

    return (
        <Button
            color="primary"
            variant="contained"
            size="large"
            fullWidth
            onClick={onOpen}
        >
            Preview
        </Button>
    )
}

export default ConfigurationFormPreview;